import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { MapPin, Mail, Phone, Building2, Briefcase, Globe, Facebook, Twitter, Instagram, Linkedin, MessageCircle, ExternalLink } from 'lucide-react';
import { apiRequest } from '../utils/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function DigitalCardProfile() {
  const { username } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchProfile();
    trackVisit();
  }, [username]);

  const trackVisit = async () => {
    try {
      // Only track visits for card profiles, not standalone digital profiles
      // Check if profile exists and is a card profile before tracking
      const response = await apiRequest(`${BACKEND_URL}/api/user/profile/${username}`, {
        method: 'GET',
      });
      
      if (response.ok && response.data) {
        // This is a card profile, track the visit
        const urlParams = new URLSearchParams(window.location.search);
        const srcId = urlParams.get('src_id');
        
        const trackingUrl = srcId 
          ? `${BACKEND_URL}/api/user/profile/${username}/track-visit?src_id=${srcId}`
          : `${BACKEND_URL}/api/user/profile/${username}/track-visit`;
        
        await fetch(trackingUrl, { method: 'POST' });
        console.log('Visit tracked successfully');
      }
    } catch (err) {
      console.log('Tracking not applicable or failed:', err);
      // Don't show error to user, tracking is optional
    }
  };

  const fetchProfile = async () => {
    try {
      // First try to fetch from digital card profiles
      let response = await apiRequest(`${BACKEND_URL}/api/user/profile/${username}`, {
        method: 'GET',
      });

      if (response.ok && response.data) {
        setProfile({ ...response.data, type: 'card_profile' });
      } else {
        // If not found, try digital profiles (standalone profiles)
        response = await apiRequest(`${BACKEND_URL}/api/user/digital-profiles/public/${username}`, {
          method: 'GET',
        });
        
        if (response.ok && response.data) {
          setProfile({ ...response.data, type: 'digital_profile' });
        } else {
          setError('Profile not found');
        }
      }
      setLoading(false);
    } catch (err) {
      setError('Failed to load profile');
      setLoading(false);
    }
  };

  const handleWhatsAppClick = () => {
    if (profile?.social_links?.whatsapp) {
      const phoneNumber = `${profile.whatsapp_country_code}${profile.social_links.whatsapp}`;
      window.open(`https://wa.me/${phoneNumber}`, '_blank');
    }
  };

  const handleSocialClick = (platform, url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const handleCustomUrlClick = (url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const saveContact = () => {
    // Create vCard
    const vcard = `BEGIN:VCARD
VERSION:3.0
FN:${profile.name}
${profile.company_name ? `ORG:${profile.company_name}` : ''}
${profile.designation ? `TITLE:${profile.designation}` : ''}
${profile.email ? `EMAIL:${profile.email}` : ''}
${profile.phone ? `TEL:${profile.phone}` : ''}
${profile.address ? `ADR:;;${profile.address}` : ''}
END:VCARD`;

    const blob = new Blob([vcard], { type: 'text/vcard' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${profile.name}.vcf`;
    link.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-navy"></div>
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Profile Not Found</h2>
          <p className="text-gray-600">The profile you're looking for doesn't exist or has been removed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50">
      {/* Mobile-optimized container */}
      <div className="max-w-md mx-auto bg-white shadow-2xl min-h-screen">
        {/* Cover Photo with gradient overlay */}
        <div className="relative h-48 bg-gradient-to-br from-navy via-purple-600 to-pink-500 overflow-hidden">
          {profile.cover_photo ? (
            <img 
              src={`${BACKEND_URL}${profile.cover_photo}`} 
              alt="Cover" 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-navy via-purple-600 to-pink-500"></div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
        </div>

        {/* Profile Photo - Overlapping cover */}
        <div className="relative px-6 -mt-20 mb-4">
          <div className="w-32 h-32 mx-auto rounded-full border-4 border-white shadow-xl overflow-hidden bg-white">
            {profile.profile_photo ? (
              <img 
                src={`${BACKEND_URL}${profile.profile_photo}`} 
                alt={profile.name} 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-navy to-purple-600 flex items-center justify-center">
                <span className="text-4xl font-bold text-white">
                  {profile.name?.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Name and Title */}
        <div className="text-center px-6 mb-6">
          <h1 className="text-3xl font-extrabold text-navy mb-2">{profile.name}</h1>
          {profile.designation && (
            <p className="text-lg text-gray-700 font-semibold">{profile.designation}</p>
          )}
          {profile.company_name && (
            <p className="text-md text-gray-600 flex items-center justify-center gap-2 mt-1">
              <Building2 className="w-4 h-4" />
              {profile.company_name}
            </p>
          )}
        </div>

        {/* Save Contact Button */}
        <div className="px-6 mb-6">
          <button
            onClick={saveContact}
            className="w-full bg-gradient-to-r from-navy to-purple-600 text-white py-4 rounded-full font-bold text-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
          >
            Save Contact
          </button>
        </div>

        {/* Contact Information */}
        <div className="px-6 mb-6 space-y-3">
          {profile.email && (
            <a 
              href={`mailto:${profile.email}`}
              className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
            >
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Mail className="w-5 h-5 text-blue-600" />
              </div>
              <span className="text-gray-800 truncate">{profile.email}</span>
            </a>
          )}

          {profile.phone && (
            <a 
              href={`tel:${profile.phone}`}
              className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
            >
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Phone className="w-5 h-5 text-green-600" />
              </div>
              <span className="text-gray-800">{profile.phone}</span>
            </a>
          )}

          {profile.address && (
            <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-red-600" />
              </div>
              <span className="text-gray-800 text-sm leading-relaxed">{profile.address}</span>
            </div>
          )}
        </div>

        {/* Social Links */}
        {(profile.social_links?.facebook || profile.social_links?.twitter || profile.social_links?.instagram || profile.social_links?.linkedin || profile.social_links?.whatsapp) && (
          <div className="px-6 mb-6">
            <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-4">Connect With Me</h3>
            <div className="flex flex-wrap gap-3 justify-center">
              {profile.social_links.facebook && (
                <button
                  onClick={() => handleSocialClick('facebook', profile.social_links.facebook)}
                  className="w-14 h-14 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-110"
                >
                  <Facebook className="w-6 h-6" fill="currentColor" />
                </button>
              )}

              {profile.social_links.twitter && (
                <button
                  onClick={() => handleSocialClick('twitter', profile.social_links.twitter)}
                  className="w-14 h-14 bg-black text-white rounded-full flex items-center justify-center hover:bg-gray-800 transition-all shadow-lg hover:shadow-xl transform hover:scale-110"
                >
                  <Twitter className="w-6 h-6" fill="currentColor" />
                </button>
              )}

              {profile.social_links.instagram && (
                <button
                  onClick={() => handleSocialClick('instagram', profile.social_links.instagram)}
                  className="w-14 h-14 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 text-white rounded-full flex items-center justify-center hover:opacity-90 transition-all shadow-lg hover:shadow-xl transform hover:scale-110"
                >
                  <Instagram className="w-6 h-6" />
                </button>
              )}

              {profile.social_links.linkedin && (
                <button
                  onClick={() => handleSocialClick('linkedin', profile.social_links.linkedin)}
                  className="w-14 h-14 bg-blue-700 text-white rounded-full flex items-center justify-center hover:bg-blue-800 transition-all shadow-lg hover:shadow-xl transform hover:scale-110"
                >
                  <Linkedin className="w-6 h-6" fill="currentColor" />
                </button>
              )}

              {profile.social_links.whatsapp && (
                <button
                  onClick={handleWhatsAppClick}
                  className="w-14 h-14 bg-green-500 text-white rounded-full flex items-center justify-center hover:bg-green-600 transition-all shadow-lg hover:shadow-xl transform hover:scale-110"
                >
                  <MessageCircle className="w-6 h-6" fill="currentColor" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Custom URLs */}
        {profile.custom_urls && profile.custom_urls.length > 0 && (
          <div className="px-6 mb-8">
            <h3 className="text-sm font-bold text-gray-600 uppercase tracking-wide mb-4">Quick Links</h3>
            <div className="space-y-3">
              {profile.custom_urls.map((link, index) => (
                <button
                  key={index}
                  onClick={() => handleCustomUrlClick(link.url)}
                  className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl hover:from-navy hover:to-purple-600 hover:text-white transition-all group shadow-sm hover:shadow-lg"
                >
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-navy group-hover:text-white" />
                    <span className="font-semibold text-gray-800 group-hover:text-white">{link.name}</span>
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-white" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 pb-8 text-center">
          <p className="text-xs text-gray-500">Powered by Smart Connections</p>
          <button
            onClick={() => window.location.href = '/'}
            className="mt-4 text-sm text-navy hover:text-purple-600 font-semibold"
          >
            Get your own digital card →
          </button>
        </div>
      </div>
    </div>
  );
}
